import { usePost } from "@albertine/api-client";
import type { RevolutPromoCodeMember } from "@albertine/types/openapi/lmt";
import { parse, stringify } from "csv/browser/esm/sync";
import { format } from "date-fns";
import { useRef, useState } from "react";
import Button from "../components/Common/Buttons";
import { Flex } from "../components/Common/Flex";
import Label from "../components/Common/Label";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";

const PROMO_CODE_COLUMN = "Promo Code";
const MAX_PROMO_CODES = 1000;
const EXPORT_HEADERS = ["Tenant", "First name", "Last name", "Email", "Phone number"];

export function FindRevolutMembersByPromoCodes() {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState("");
    const [promoCodes, setPromoCodes] = useState<string[]>([]);
    const [fileError, setFileError] = useState("");
    const { mutate, isPending, data, error, reset } = usePost(
        "findRevolutMembersByPromoCodes",
        {
            onSuccess: ({ members }) => {
                downloadMembersCsv(members);
            },
        },
    );

    const handleFileChange = async (file: File | undefined) => {
        setPromoCodes([]);
        setFileError("");
        reset();
        setFileName(file?.name ?? "");
        if (!file) return;

        try {
            const codes = parsePromoCodes(await file.text());
            if (codes.length > MAX_PROMO_CODES) {
                setFileError(
                    `The file has ${codes.length} promo codes. Maximum is ${MAX_PROMO_CODES}.`,
                );
                return;
            }
            setPromoCodes(codes);
        } catch (parseError) {
            setFileError(
                parseError instanceof Error ? parseError.message : String(parseError),
            );
        }
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(event) => {
                    handleFileChange(event.target.files?.[0]);
                    event.target.value = "";
                }}
            />
            <Flex.Row gap="medium">
                <Button
                    label={fileName ? "Choose another file" : "Choose CSV file"}
                    onClick={() => fileInputRef.current?.click()}
                />
                {fileName && (
                    <span>
                        {fileName}
                        {promoCodes.length > 0 && ` · ${promoCodes.length} promo codes`}
                    </span>
                )}
            </Flex.Row>
            {fileError && <p>{fileError}</p>}
            <Button.Secondary
                label="Find members and download CSV"
                showLoadingSpinner={isPending}
                disabled={promoCodes.length === 0 || isPending}
                onClick={() => {
                    mutate({ json: { promoCodes } });
                }}
            />

            {(isPending || data || error) && <Label.Large.N10>Results</Label.Large.N10>}

            {isPending && <LoadingSpinner />}
            {!isPending && data && (
                <pre>
                    {`Matched ${data.members.length} member(s).`}
                    {data.unmatchedPromoCodes.length > 0 &&
                        `\nNo member found for ${data.unmatchedPromoCodes.length} promo code(s):\n${data.unmatchedPromoCodes.join("\n")}`}
                </pre>
            )}
            {!isPending && error && <pre>{JSON.stringify(error, undefined, 2)}</pre>}
        </>
    );
}

function parsePromoCodes(content: string): string[] {
    const records: Record<string, string>[] = parse(content, {
        columns: true,
        skip_empty_lines: true,
        trim: true,
        bom: true,
    });
    if (records.length > 0 && !(PROMO_CODE_COLUMN in records[0])) {
        throw new Error(`The file has no "${PROMO_CODE_COLUMN}" column.`);
    }
    return [
        ...new Set(records.map((record) => record[PROMO_CODE_COLUMN]).filter(Boolean)),
    ];
}

function downloadMembersCsv(members: RevolutPromoCodeMember[]) {
    const csv = stringify([
        EXPORT_HEADERS,
        ...members.map((member) => [
            member.tenant,
            member.firstName,
            member.lastName,
            member.email,
            member.phoneNumber,
        ]),
    ]);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `revolut-members-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}
